<script setup lang="ts">
import type { SelectTriggerProps } from 'reka-ui';
import type { HTMLAttributes } from 'vue';
import type { ControlSize } from '@/components/base/_internal/control';
import { reactiveOmit } from '@vueuse/core';
import { ChevronDown, X } from 'lucide';
import { SelectIcon, SelectTrigger, useForwardProps } from 'reka-ui';
import { SphIcon } from '@/components/base/icon/';
import { useI18n } from '@/composables/i18n';
import { cn } from '@/utils/shadcn/lib/utils.ts';

defineOptions({
    compatConfig: {
        MODE: 3,
    },
    inheritAttrs: false,
});

const props = withDefaults(defineProps<SelectTriggerProps & {
    class?: HTMLAttributes['class']
    id?: string
    size?: ControlSize
    /** Fills the select's root instead of following the selected label. */
    fill?: boolean
    hasClearButton?: boolean
    showClearButton?: boolean
    placeholder?: string
}>(), {
    class: '',
    id: undefined,
    size: 'md',
    fill: false,
    hasClearButton: false,
    showClearButton: false,
    placeholder: undefined,
});

const emit = defineEmits<{
    clear: []
}>();

const { i18n } = useI18n();

const delegatedProps = reactiveOmit(props, 'class', 'size', 'fill');
const forwardedProps = useForwardProps(delegatedProps);
</script>

<template>
    <!-- Unless it fills, w-fit keeps the trigger at content width, and max-w-full stops it from
         growing past whatever width the wrapping .sph-select was given (e.g. class="w-56") -->
    <div
        class="relative max-w-full"
        :class="props.fill ? 'w-full' : 'w-[var(--control-width-stretch,fit-content)]'"
    >
        <SelectTrigger
            v-bind="{ ...forwardedProps, ...$attrs }"
            data-slot="select-trigger"
            :data-size="size"
            class="sph-form-control"
            :class="cn(
                'bg-input border-input-dark text-input-foreground data-placeholder:text-muted-foreground font-normal [&_svg:not([class*=\'text-\'])]:text-muted-foreground  w-full flex items-center justify-between gap-0 min-w-0 overflow-hidden rounded-md border px-2 py-0 whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none text-base h-(--input-height) data-[size=sm]:h-(--input-height-small) data-[size=sm]:text-sm data-[size=lg]:h-(--button-height-lg) *:data-[slot=select-value]:line-clamp-1',
                props.class,
            )"
        >
            <div
                class="content"
                :data-placeholder-text="props.placeholder"
                :class="hasClearButton ? 'mr-5' : ''"
            >
                <slot></slot>
            </div>
            <SelectIcon as-child>
                <SphIcon
                    :icon="ChevronDown"
                    size="sm"
                    class="flex-none opacity-50"
                />
            </SelectIcon>
        </SelectTrigger>
        <div
            v-if="hasClearButton"
            :aria-label="i18n.t('Auswahl löschen')"
            tabindex="0"
            role="button"
            class="clear-button"
            :class="{ show: showClearButton }"
            @click.prevent.stop="emit('clear')"
            @keydown.enter.prevent.stop="emit('clear')"
            @keydown.space.prevent.stop="emit('clear')"
        >
            <SphIcon
                :icon="X"
                size="sm"
            />
        </div>
    </div>
</template>

<style scoped>
@reference '@/assets/styles/config.css';

[data-slot='select-trigger'] {
    > .content {
        display: block;
        /* without this the flex item's automatic minimum size is the nowrap text width, so it
           never shrinks and the trigger grows past the width it was given */
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        &::before {
            content: attr(data-placeholder-text);
            display: block;
            height: 0;
            overflow: hidden;
            visibility: hidden;
            white-space: nowrap;
        }

        /* An icon in the display slot is inline content of the value's line box. On the baseline
           it stacks on the font's descender and the line box grows to ~26px with the icon at its
           top, 2.5px above centre; hung from the top it fits the 21px line box and both the icon
           and any text beside it come out centred. */
        :deep(.sph-icon) {
            vertical-align: top;
        }
    }
}
</style>

<style scoped>
@reference '@/assets/styles/config.css';

.clear-button {
    @apply absolute right-7 top-0 z-10 w-5 h-full flex items-center justify-center rounded-sm cursor-pointer text-muted-foreground hover:text-accent transition-all border border-transparent focus-visible:border-ring/50 outline-none select-none pointer-events-none opacity-0;

    &.show {
        @apply opacity-100 pointer-events-auto;
    }
}
</style>
